/**
 * Onboarding job lifecycle composable.
 *
 * Coordinates the three Lambda calls behind /admin/onboard:
 *   1. POST /admin/onboard                                  -> kickoff
 *   2. GET  /admin/onboard/jobs/{job_id}                    -> parent status
 *   3. GET  /admin/onboard/jobs/{job_id}/children           -> per-sprint progress
 *
 * Polls the parent every PARENT_INTERVAL_MS and the children every
 * CHILDREN_INTERVAL_MS until the parent reaches a terminal state, then
 * stops both timers. Cleans up on unmount.
 */
import { onScopeDispose, ref, shallowRef } from "vue";

import {
  fetchOnboardChildren,
  fetchOnboardJob,
  triggerOnboard,
  type OnboardChildJob,
  type OnboardJob,
  type OnboardKickoffRequest,
  type OnboardKickoffResponse,
} from "@/api/admin";
import { useApi } from "@/composables/useApi";

const PARENT_INTERVAL_MS = 3_000;
const CHILDREN_INTERVAL_MS = 5_000;

/** A non-recoverable phase the SPA should render distinctly. */
export type OnboardPhase = "idle" | "starting" | "polling" | "done" | "error";

export function useOnboardJob() {
  const { client } = useApi();

  const phase = ref<OnboardPhase>("idle");
  const errorMessage = ref<string | null>(null);

  const kickoff = shallowRef<OnboardKickoffResponse | null>(null);
  const job = shallowRef<OnboardJob | null>(null);
  const children = shallowRef<OnboardChildJob[]>([]);

  let parentTimer: ReturnType<typeof setInterval> | null = null;
  let childrenTimer: ReturnType<typeof setInterval> | null = null;
  let stopped = false;

  function clearTimers() {
    if (parentTimer !== null) {
      clearInterval(parentTimer);
      parentTimer = null;
    }
    if (childrenTimer !== null) {
      clearInterval(childrenTimer);
      childrenTimer = null;
    }
  }

  function stop() {
    stopped = true;
    clearTimers();
  }

  function isTerminal(j: OnboardJob | null): boolean {
    return j?.status === "completed";
  }

  async function pollParent(jobId: string) {
    if (stopped) return;
    try {
      const next = await fetchOnboardJob(client, jobId);
      job.value = next;
      if (isTerminal(next)) {
        // One last children fetch to capture late completions.
        try { children.value = await fetchOnboardChildren(client, jobId); } catch { /* swallow */ }
        phase.value = "done";
        clearTimers();
      }
    } catch (err) {
      // Transient errors during polling shouldn't abort the whole UX —
      // surface but keep retrying. A 404 means Windmill purged the job;
      // give up cleanly.
      const status = (err as { status?: number }).status;
      if (status === 404) {
        errorMessage.value = "Windmill no longer has this job — it may have expired.";
        phase.value = "error";
        clearTimers();
      } else {
        // Soft failure: leave phase=polling so the UI keeps spinning.
        // eslint-disable-next-line no-console
        console.warn("[useOnboardJob] parent poll failed", err);
      }
    }
  }

  async function pollChildren(jobId: string) {
    if (stopped) return;
    try {
      children.value = await fetchOnboardChildren(client, jobId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[useOnboardJob] children poll failed", err);
    }
  }

  /** Kick off onboarding. Resolves once the kickoff returns; the lifecycle
   * then transitions to "polling" automatically. */
  async function start(input: OnboardKickoffRequest): Promise<void> {
    if (phase.value === "starting" || phase.value === "polling") {
      return;
    }
    stopped = false;
    phase.value = "starting";
    errorMessage.value = null;
    kickoff.value = null;
    job.value = null;
    children.value = [];

    let resp: OnboardKickoffResponse;
    try {
      resp = await triggerOnboard(client, input);
    } catch (err) {
      const status = (err as { status?: number }).status;
      const message = (err as { message?: string }).message ?? "Failed to trigger onboarding.";
      errorMessage.value = status === 429
        ? "Cooldown active for this board — try again in a minute."
        : message;
      phase.value = "error";
      return;
    }

    kickoff.value = resp;
    phase.value = "polling";

    await pollParent(resp.job_id);
    if (phase.value !== "polling") return; // already terminal / errored

    // Parallel polling — slightly different cadences so the network isn't
    // synchronised between parent + children calls.
    parentTimer = setInterval(() => { void pollParent(resp.job_id); }, PARENT_INTERVAL_MS);
    childrenTimer = setInterval(() => { void pollChildren(resp.job_id); }, CHILDREN_INTERVAL_MS);

    // Fire one immediate children fetch alongside the timer.
    void pollChildren(resp.job_id);
  }

  onScopeDispose(stop);

  return {
    phase,
    errorMessage,
    kickoff,
    job,
    children,
    start,
    stop,
  };
}
